import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Copy,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ProviderLogo } from "@/components/provider-logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/auth-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useCreateProductionMachine,
  useDeleteProductionMachine,
  useProductionMachines,
  useUpdateProductionMachine,
} from "@/hooks/use-production-machines";
import { copyText } from "@/lib/clipboard";
import type { ProductionMachine } from "@/lib/types";
import { cn, formatRelativeTime } from "@/lib/utils";

// ── Sorting ──

type SortKey = "client_name" | "endpoint" | "machine" | "server" | "model" | "updated_at";
type SortDirection = "asc" | "desc";

function sortValue(machine: ProductionMachine, key: SortKey): string | number {
  switch (key) {
    case "client_name":
      return (machine.client_name ?? "").toLowerCase();
    case "endpoint":
      return (machine.endpoint ?? "").toLowerCase();
    case "machine":
      return (machine.machine_name ?? machine.instance_id ?? "").toLowerCase();
    case "server":
      return (machine.server ?? "").toLowerCase();
    case "model":
      return (machine.model ?? "").toLowerCase();
    case "updated_at":
      return new Date(machine.updated_at).getTime();
  }
}

function SortIcon({
  column,
  sortKey,
  sortDirection,
}: {
  column: SortKey;
  sortKey: SortKey | null;
  sortDirection: SortDirection;
}) {
  if (sortKey !== column) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/40" />;
  }
  return sortDirection === "asc" ? (
    <ArrowUp className="ml-1 inline h-3 w-3 text-foreground/70" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3 text-foreground/70" />
  );
}

// ── Small cells ──

/** Muted em dash for empty values. */
function Dash() {
  return <span className="text-muted-foreground/50">—</span>;
}

/** Monospace value with click-to-copy; shows a brief check on success. Empty → muted em dash. */
function CopyValue({ value, className }: { value: string | null; className?: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <Dash />;
  return (
    <button
      type="button"
      title="Click to copy"
      onClick={async () => {
        if (await copyText(value)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      }}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 font-mono text-[12px] text-foreground/90 transition-[color,transform] hover:text-foreground active:scale-[0.98]",
        className,
      )}
    >
      <span className="truncate">{value}</span>
      {copied ? (
        <Check className="h-3 w-3 shrink-0 text-emerald-500" />
      ) : (
        <Copy className="h-3 w-3 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
      )}
    </button>
  );
}

function textOrDash(value: string | null) {
  return value ? <span className="text-foreground/90">{value}</span> : <Dash />;
}

// ── Edit / create dialog ──

type DialogState = { mode: "create" } | { mode: "edit"; machine: ProductionMachine };

const TEXTAREA_CLASS = cn(
  "w-full resize-y rounded-lg border border-input bg-transparent px-2.5 py-2 font-mono text-[12px] leading-relaxed outline-none transition-colors",
  "placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
  "dark:bg-input/30",
);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ProductionMachineDialog({
  state,
  onOpenChange,
}: {
  state: DialogState | null;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = state?.mode === "edit";
  const existing = state?.mode === "edit" ? state.machine : null;
  const create = useCreateProductionMachine();
  const update = useUpdateProductionMachine();

  // Metadata (editable in both modes).
  const [clientName, setClientName] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [server, setServer] = useState("");
  const [model, setModel] = useState("");
  const [smallNote, setSmallNote] = useState("");
  const [deploymentNotes, setDeploymentNotes] = useState("");
  // Identity (create-only).
  const [instanceId, setInstanceId] = useState("");
  const [machineName, setMachineName] = useState("");
  const [provider, setProvider] = useState("");

  // Re-seed the form whenever the dialog opens for a different row/mode.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed on open only
  useEffect(() => {
    if (!state) return;
    setClientName(existing?.client_name ?? "");
    setEndpoint(existing?.endpoint ?? "");
    setServer(existing?.server ?? "");
    setModel(existing?.model ?? "");
    setSmallNote(existing?.small_note ?? "");
    setDeploymentNotes(existing?.deployment_notes ?? "");
    setInstanceId("");
    setMachineName("");
    setProvider("");
  }, [state]);

  const orNull = (value: string) => {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const metadata = {
      client_name: orNull(clientName),
      endpoint: orNull(endpoint),
      server: orNull(server),
      model: orNull(model),
      small_note: orNull(smallNote),
      deployment_notes: orNull(deploymentNotes),
    };
    if (existing) {
      update.mutate({ id: existing.id, body: metadata }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(
        {
          ...metadata,
          instance_id: orNull(instanceId),
          machine_name: orNull(machineName),
          provider: orNull(provider),
        },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={state !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit production machine" : "Add production machine"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update how this endpoint is deployed. Changes are visible to everyone."
              : "Register a production endpoint. A cloud instance ID excludes it from the Fleet pool; leave it blank for external infrastructure."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          {isEdit && existing && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-[12px]">
              {existing.provider && <ProviderLogo provider={existing.provider} />}
              <span className="font-medium text-foreground">
                {existing.machine_name ?? "manual entry"}
              </span>
              {existing.instance_id && (
                <code className="truncate font-mono text-[11px] text-muted-foreground">
                  {existing.instance_id}
                </code>
              )}
            </div>
          )}

          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Instance ID (optional)">
                <Input
                  value={instanceId}
                  onChange={(event) => setInstanceId(event.target.value)}
                  placeholder="computeinstance-… / GCP name"
                  className="h-8 font-mono text-[12px]"
                />
              </Field>
              <Field label="Provider (optional)">
                <Input
                  value={provider}
                  onChange={(event) => setProvider(event.target.value)}
                  placeholder="nebius / gcp / external"
                  className="h-8 text-[12px]"
                />
              </Field>
              <Field label="Machine name (optional)">
                <Input
                  value={machineName}
                  onChange={(event) => setMachineName(event.target.value)}
                  placeholder="display name"
                  className="h-8 text-[12px]"
                />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Client">
              <Input
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
                placeholder="who it serves"
                className="h-8 text-[12px]"
              />
            </Field>
            <Field label="Model">
              <Input
                value={model}
                onChange={(event) => setModel(event.target.value)}
                placeholder="running model"
                className="h-8 text-[12px]"
              />
            </Field>
            <Field label="Endpoint">
              <Input
                value={endpoint}
                onChange={(event) => setEndpoint(event.target.value)}
                placeholder="https://…"
                className="h-8 font-mono text-[12px]"
              />
            </Field>
            <Field label="Server">
              <Input
                value={server}
                onChange={(event) => setServer(event.target.value)}
                placeholder="host / node"
                className="h-8 text-[12px]"
              />
            </Field>
          </div>

          <Field label="Short note">
            <Input
              value={smallNote}
              onChange={(event) => setSmallNote(event.target.value)}
              placeholder="one-liner"
              className="h-8 text-[12px]"
            />
          </Field>

          <Field label="Deployment notes">
            <textarea
              value={deploymentNotes}
              onChange={(event) => setDeploymentNotes(event.target.value)}
              placeholder="How it's deployed — commands, image tags, env, runbook…"
              rows={7}
              className={TEXTAREA_CLASS}
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {isEdit ? "Save" : "Add machine"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ──

export function ProductionMachinesPage() {
  usePageTitle("Production Machines");
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const isMobile = useIsMobile();

  const { data: machines, isLoading } = useProductionMachines();
  const deleteMachine = useDeleteProductionMachine();

  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>("client_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProductionMachine | null>(null);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const rows = useMemo(() => {
    const all = machines ?? [];
    const needle = filter.trim().toLowerCase();
    const filtered = needle
      ? all.filter((machine) =>
          [
            machine.client_name,
            machine.endpoint,
            machine.server,
            machine.model,
            machine.machine_name,
            machine.instance_id,
            machine.small_note,
            machine.deployment_notes,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle),
        )
      : [...all];
    if (sortKey) {
      filtered.sort((a, b) => {
        const av = sortValue(a, sortKey);
        const bv = sortValue(b, sortKey);
        const order = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDirection === "asc" ? order : -order;
      });
    }
    return filtered;
  }, [machines, filter, sortKey, sortDirection]);

  const total = machines?.length ?? 0;

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[17px] font-semibold text-foreground">Production Machines</h1>
          <p className="mt-0.5 max-w-2xl text-pretty text-[12px] text-muted-foreground">
            Machines serving production endpoints — excluded from the Fleet pool. Read-only here: no
            acquire, stop, or lifecycle actions. Anyone can edit the metadata and deployment notes;
            only admins add or remove entries.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter…"
              aria-label="Filter production machines"
              className="h-8 pl-8 pr-8 text-[13px]"
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter("")}
                aria-label="Clear filter"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => setDialogState({ mode: "create" })}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="py-16 text-center text-[13px] text-muted-foreground">Loading…</div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
          <p className="text-[13px] text-muted-foreground">No production machines yet.</p>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => setDialogState({ mode: "create" })}
            >
              <Plus className="h-3.5 w-3.5" />
              Add one
            </Button>
          )}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {rows.map((machine) => (
            <MachineCard
              key={machine.id}
              machine={machine}
              isAdmin={isAdmin}
              onEdit={() => setDialogState({ mode: "edit", machine })}
              onDelete={() => setPendingDelete(machine)}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortableHead
                  label="Client"
                  column="client_name"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <SortableHead
                  label="Endpoint"
                  column="endpoint"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <SortableHead
                  label="Machine"
                  column="machine"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <SortableHead
                  label="Server"
                  column="server"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <SortableHead
                  label="Model"
                  column="model"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <TableHead className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Note
                </TableHead>
                <TableHead className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Deploy
                </TableHead>
                <SortableHead
                  label="Updated"
                  column="updated_at"
                  {...{ sortKey, sortDirection, toggleSort }}
                />
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((machine) => (
                <TableRow key={machine.id} className="group transition-colors hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground">
                    {textOrDash(machine.client_name)}
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <CopyValue value={machine.endpoint} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {machine.provider && <ProviderLogo provider={machine.provider} />}
                      <span className="max-w-[160px] truncate text-[12px] text-foreground/90">
                        {machine.machine_name ?? (
                          <code className="font-mono text-[11px] text-muted-foreground">
                            {machine.instance_id ?? "manual"}
                          </code>
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate text-[12px]">
                    {textOrDash(machine.server)}
                  </TableCell>
                  <TableCell>
                    {machine.model ? (
                      <span className="inline-block rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-foreground/80">
                        {machine.model}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate text-[12px] text-muted-foreground">
                    {machine.small_note ?? ""}
                  </TableCell>
                  <TableCell>
                    <DeployCell
                      machine={machine}
                      onOpen={() => setDialogState({ mode: "edit", machine })}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums text-[12px] text-muted-foreground">
                    {formatRelativeTime(new Date(machine.updated_at))}
                  </TableCell>
                  <TableCell className="w-0 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-0.5 opacity-60 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Edit"
                        onClick={() => setDialogState({ mode: "edit", machine })}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          aria-label="Remove"
                          onClick={() => setPendingDelete(machine)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ProductionMachineDialog
        state={dialogState}
        onOpenChange={(open) => !open && setDialogState(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Remove ${pendingDelete?.client_name ?? pendingDelete?.machine_name ?? "this entry"}?`}
        description="Removes it from the registry. If it maps to a cloud instance, that machine re-enters the Fleet pool on the next sync. The cloud instance is not touched."
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={() => {
          if (pendingDelete) deleteMachine.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

function SortableHead({
  label,
  column,
  sortKey,
  sortDirection,
  toggleSort,
}: {
  label: string;
  column: SortKey;
  sortKey: SortKey | null;
  sortDirection: SortDirection;
  toggleSort: (key: SortKey) => void;
}) {
  return (
    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      <button
        type="button"
        onClick={() => toggleSort(column)}
        className="inline-flex items-center transition-colors hover:text-foreground"
      >
        {label}
        <SortIcon column={column} sortKey={sortKey} sortDirection={sortDirection} />
      </button>
    </TableHead>
  );
}

function DeployCell({ machine, onOpen }: { machine: ProductionMachine; onOpen: () => void }) {
  const has = Boolean(machine.deployment_notes?.trim());
  return (
    <button
      type="button"
      onClick={onOpen}
      title={has ? (machine.deployment_notes ?? "") : "Add deployment notes"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-[color,background-color,transform] active:scale-[0.97]",
        has
          ? "border-border bg-muted/40 text-foreground/80 hover:bg-muted"
          : "border-dashed border-border text-muted-foreground/60 hover:text-foreground",
      )}
    >
      <FileText className="h-3 w-3" />
      {has ? "notes" : "add"}
    </button>
  );
}

function MachineCard({
  machine,
  isAdmin,
  onEdit,
  onDelete,
}: {
  machine: ProductionMachine;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[14px] font-medium text-foreground">
            {machine.client_name ?? <Dash />}
          </div>
          <div className="mt-0.5">
            <CopyValue value={machine.endpoint} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Edit"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              aria-label="Remove"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        {machine.provider && <ProviderLogo provider={machine.provider} />}
        {machine.machine_name && <span className="text-foreground/80">{machine.machine_name}</span>}
        {machine.model && (
          <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-foreground/80">
            {machine.model}
          </span>
        )}
        {machine.server && <span>· {machine.server}</span>}
      </div>
      {machine.small_note && (
        <div className="mt-2 text-[12px] text-muted-foreground">{machine.small_note}</div>
      )}
    </div>
  );
}
